import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, Play } from "lucide-react";

import { Button } from "@/design-system/primitives";
import { track } from "@/lib/analytics";
import { markWelcomeVideoDone } from "@/lib/welcomeVideoFlags";
import "./WelcomeVideo.css";

const VIDEO_URL = "/videos/dr-shoma-welcome-v2.mp4";
const POSTER_URL = "/videos/dr-shoma-welcome-v2.jpg";
// The editor's replacement retains the original speech timing.
const CAPTIONS_URL = "/videos/dr-shoma-welcome-v1.en.vtt";
const VIDEO_ANALYTICS_ID = "dr_shoma_v2";

/** Mounted only after ID/consent, independently of the guided-tour cohort. */
export function WelcomeVideo({
  patientId,
  preview = false,
  onContinue,
}: {
  patientId: string;
  preview?: boolean;
  onContinue?: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const started = useRef(false);
  const completed = useRef(false);
  const viewed = useRef(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [error, setError] = useState(false);
  const [captionsLoaded, setCaptionsLoaded] = useState(false);
  const [captionText, setCaptionText] = useState("");
  const [nativePresentation, setNativePresentation] = useState(false);

  useEffect(() => {
    const element = video.current;
    const captions = element?.textTracks[0];
    if (!element || !captions || !captionsLoaded) return;

    const syncCaptions = () => {
      const enabled = captions.mode === "showing";
      setCaptionText(
        enabled
          ? Array.from(captions.activeCues ?? [])
              .map((cue) => (cue as VTTCue).text)
              .join("\n")
          : "",
      );
    };
    const syncPresentation = () => {
      const safari = element as HTMLVideoElement & {
        webkitDisplayingFullscreen?: boolean;
        webkitPresentationMode?: string;
      };
      setNativePresentation(
        document.fullscreenElement === element ||
          document.pictureInPictureElement === element ||
          safari.webkitDisplayingFullscreen === true ||
          safari.webkitPresentationMode === "fullscreen" ||
          safari.webkitPresentationMode === "picture-in-picture",
      );
    };
    const enterNative = () => setNativePresentation(true);
    const exitNative = () => setNativePresentation(false);
    captions.addEventListener("cuechange", syncCaptions);
    element.textTracks.addEventListener("change", syncCaptions);
    document.addEventListener("fullscreenchange", syncPresentation);
    element.addEventListener("webkitbeginfullscreen", enterNative);
    element.addEventListener("webkitendfullscreen", exitNative);
    element.addEventListener("webkitpresentationmodechanged", syncPresentation);
    element.addEventListener("enterpictureinpicture", enterNative);
    element.addEventListener("leavepictureinpicture", exitNative);
    syncCaptions();
    syncPresentation();
    return () => {
      captions.removeEventListener("cuechange", syncCaptions);
      element.textTracks.removeEventListener("change", syncCaptions);
      document.removeEventListener("fullscreenchange", syncPresentation);
      element.removeEventListener("webkitbeginfullscreen", enterNative);
      element.removeEventListener("webkitendfullscreen", exitNative);
      element.removeEventListener(
        "webkitpresentationmodechanged",
        syncPresentation,
      );
      element.removeEventListener("enterpictureinpicture", enterNative);
      element.removeEventListener("leavepictureinpicture", exitNative);
    };
  }, [captionsLoaded]);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    track("onboarding_welcome_video_viewed", {
      video: VIDEO_ANALYTICS_ID,
      started_via: preview ? "qa_param" : "auto",
    });
  }, []);

  function finish() {
    video.current?.pause();
    track("onboarding_welcome_video_continue", {
      video: VIDEO_ANALYTICS_ID,
      started_via: preview ? "qa_param" : "auto",
      started: started.current,
      completed: completed.current,
    });
    if (onContinue) onContinue();
    else markWelcomeVideoDone(patientId);
  }

  async function play() {
    try {
      await video.current?.play();
    } catch {
      // Playback failure must never trap the patient in onboarding.
      setError(true);
    }
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) finish();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-ink/40 backdrop-blur-[6px]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-dialog flex max-h-[calc(100dvh-30px)] w-[min(76rem,calc(100vw-30px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border border-line-subtle bg-page text-ink shadow-modal"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            heading.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = document.querySelector<HTMLElement>("main h1, main");
            if (target) {
              if (!target.hasAttribute("tabindex"))
                target.setAttribute("tabindex", "-1");
              target.focus();
            }
          }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <div className="min-h-0 overflow-y-auto">
            <header className="px-20 pb-20 pt-25 md:px-30 md:pt-30">
              <p className="type-label-xs text-ink-secondary">
                Your care starts here · 1 minute
              </p>
              <Dialog.Title
                ref={heading}
                tabIndex={-1}
                // This static heading receives initial screen-reader focus;
                // keyboard focus indicators remain on the actionable controls.
                className="mt-12 type-headline-lg font-normal focus:outline-none focus:shadow-none"
              >
                Welcome to System
              </Dialog.Title>
              <Dialog.Description className="mt-15 font-body text-15 font-normal text-ink-secondary leading-150">
                Personalized care, built around you. Meet Dr. Shoma and get
                ready for the next step in your health journey.
              </Dialog.Description>
              <div className="mt-20 border-t border-line-subtle pt-15">
                <p className="font-body text-15 text-ink leading-140">
                  Dr. Shoma Datta-Thomas, MD, FACOG
                </p>
                <p className="mt-5 font-body text-12 text-ink-secondary leading-150">
                  OB-GYN residency, Johns Hopkins
                </p>
              </div>
            </header>
            <div className="welcome-video-frame relative mx-15 md:mx-30 overflow-hidden border border-line-subtle bg-sunken">
              <video
                ref={video}
                src={VIDEO_URL}
                poster={POSTER_URL}
                controls={hasPlayed}
                playsInline
                preload="none"
                aria-label="Welcome to System from Dr. Shoma"
                className={`welcome-video block aspect-[8/5] w-full${captionsLoaded && !nativePresentation ? " welcome-video--custom-captions" : ""}`}
                onPlay={() => {
                  setHasPlayed(true);
                  setError(false);
                  if (!started.current) {
                    started.current = true;
                    track("onboarding_welcome_video_started", {
                      video: VIDEO_ANALYTICS_ID,
                      started_via: preview ? "qa_param" : "auto",
                    });
                  }
                }}
                onEnded={() => {
                  if (!completed.current) {
                    completed.current = true;
                    track("onboarding_welcome_video_completed", {
                      video: VIDEO_ANALYTICS_ID,
                      started_via: preview ? "qa_param" : "auto",
                    });
                  }
                }}
                onError={() => setError(true)}
              >
                <track
                  kind="captions"
                  src={CAPTIONS_URL}
                  srcLang="en"
                  label="English"
                  default
                  onLoadCapture={() => setCaptionsLoaded(true)}
                  onErrorCapture={() => setCaptionsLoaded(false)}
                />
              </video>
              {hasPlayed &&
                captionsLoaded &&
                !error &&
                captionText &&
                !nativePresentation && (
                  <div className="welcome-video-captions" aria-hidden="true">
                    <span>{captionText}</span>
                  </div>
                )}
              {!hasPlayed && !error && (
                <button
                  type="button"
                  onClick={() => {
                    void play();
                  }}
                  aria-label="Play welcome video"
                  className="absolute inset-0 flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-accent"
                >
                  <span className="flex h-60 w-60 items-center justify-center rounded-full border border-line-subtle bg-page text-ink shadow-modal">
                    <Play size={24} fill="currentColor" aria-hidden />
                  </span>
                </button>
              )}
            </div>
            <p
              className="px-20 py-15 md:px-30 type-body-sm text-ink-secondary leading-150"
              role={error ? "alert" : undefined}
            >
              {error
                ? "The video couldn’t play. You can still continue to your portal."
                : "Watch with sound, or continue whenever you’re ready."}
            </p>
          </div>
          <footer className="shrink-0 border-t border-line-subtle px-20 py-15 md:px-30 md:py-20">
            <Button variant="filled" onClick={finish}>
              Continue to my portal <ArrowRight size={18} aria-hidden />
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
